// src/components/Planner/PlannerViews/TaskEditorModal.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePlanner } from "../../../contexts/PlannerContext";
import { useStaff as useStaffHook, StaffContext } from "../../../contexts/StaffContext";

/* ---------------------- helpers ---------------------- */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const P = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${P(d.getMonth() + 1)}-${P(d.getDate())}T${P(d.getHours())}:${P(d.getMinutes())}`;
}
function fromLocalInput(v) { return v ? new Date(v).toISOString() : null; }
function roundUp(date, stepMin = 30) {
  const ms = stepMin * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}
function defaultDraft() {
  const start = roundUp(new Date(), 30);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    id: undefined,
    title: "",
    batch: "",
    start: toLocalInput(start.toISOString()),
    end: toLocalInput(end.toISOString()),
    stage: "",
    qty: 1,
    category: "",
    taskType: "",
    productId: "",
    assigneeId: "",
    color: "#f97316",
    notes: "",
    tags: [],
  };
}

/* ---------- Stage normalization (match ListView) ---------- */
const STAGE_LABELS = ["Forming","Drying","Bisque","Glazing","Glaze Firing","QC & Pack","Dispatch"];
const LABEL_TO_CANON = {
  "Forming":"forming",
  "Drying":"drying",
  "Bisque":"bisque",
  "Glazing":"glazing",
  "Glaze Firing":"glazeFiring",
  "QC & Pack":"qcPack",
  "Dispatch":"dispatch",
};
const CANON_TO_LABEL = {
  forming:"Forming",
  drying:"Drying",
  bisque:"Bisque",
  glazing:"Glazing",
  glazeFiring:"Glaze Firing",
  qcPack:"QC & Pack",
  dispatch:"Dispatch",
};
const STAGE_KEYS = new Set(Object.keys(CANON_TO_LABEL));

function stageLabelFromEvent(ev) {
  const tag = (Array.isArray(ev?.tags) ? ev.tags : []).find(t => STAGE_KEYS.has(t));
  if (tag && CANON_TO_LABEL[tag]) return CANON_TO_LABEL[tag];
  const st = String(ev?.stage || "").trim();
  if (!st) return "";
  if (LABEL_TO_CANON[st]) return st;
  if (CANON_TO_LABEL[st]) return CANON_TO_LABEL[st];
  if (st.replace(/\s+/g, "") === "GlazeFiring") return "Glaze Firing";
  const lc = st.charAt(0).toLowerCase() + st.slice(1);
  if (CANON_TO_LABEL[lc]) return CANON_TO_LABEL[lc];
  return "";
}
function buildStagePatch(currentTags, label) {
  const canon = LABEL_TO_CANON[label] || undefined;
  const baseTags = Array.isArray(currentTags) ? currentTags : [];
  const nonStage = baseTags.filter(t => !STAGE_KEYS.has(t));
  const tags = canon ? [...nonStage, canon] : nonStage;
  return { stage: label || undefined, tags: tags.length ? tags : undefined };
}

/* ---------- Flexible deleter (works with different Planner APIs) ---------- */
function callDeleteAny(planner, id) {
  if (!planner || !id) return false;
  const candidates = [
    "removeEventById",
    "deleteEventById",
    "removeTaskById",
    "deleteTaskById",
    "deleteEvent",
    "removeEvent",
    "deleteTask",
    "removeTask",
    "deleteEvents",
    "removeEvents",
  ];
  for (const key of candidates) {
    const fn = planner[key];
    if (typeof fn !== "function") continue;
    try { fn(id); return true; } catch {}
    try { fn([id]); return true; } catch {}
    try { fn({ id }); return true; } catch {}
  }
  console.warn("Planner delete function not found for id:", id, planner);
  return false;
}

/* ---------------------- component ---------------------- */
export default function TaskEditorModal({ open, event, onClose }) {
  const planner = usePlanner();
  const { upsertEvents, events } = planner;

  /* ---- Staff: try hook, context, then localStorage ---- */
  let hookState = null;
  try { hookState = useStaffHook(); } catch {}
  const ctxState = (() => { try { return useContext(StaffContext); } catch { return null; } })();

  const staffFromHook =
    (hookState && Array.isArray(hookState.staff) && hookState.staff) ||
    (hookState && Array.isArray(hookState.staffList) && hookState.staffList) ||
    [];

  const staffFromCtx =
    (ctxState && Array.isArray(ctxState.staff) && ctxState.staff) ||
    (ctxState && Array.isArray(ctxState.staffList) && ctxState.staffList) ||
    [];

  const staffFromLS = (() => {
    try {
      const raw = localStorage.getItem("mudoStaff");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  })();

  const staffAll = (staffFromHook.length ? staffFromHook : (staffFromCtx.length ? staffFromCtx : staffFromLS));

  const staffOptions = useMemo(() => {
    return (staffAll || [])
      .map((s) => {
        const idCandidate =
          (s && s.id !== undefined ? s.id : undefined) ??
          (s && s.staffId !== undefined ? s.staffId : undefined) ??
          (s && s.email !== undefined ? s.email : undefined) ??
          (s && s.name ? s.name.toLowerCase().replace(/\s+/g, "-") : undefined);

        const first = (s && s.firstName) ?? "";
        const last = (s && s.lastName) ?? "";
        const composed = (first + " " + last).trim();
        const labelCandidate =
          (s && s.name) ??
          (s && s.fullName) ??
          (composed.length ? composed : undefined) ??
          (s && s.email) ??
          String(idCandidate ?? "");

        const id = String(idCandidate ?? "");
        const label = String(labelCandidate);
        return id && label ? { id, label } : null;
      })
      .filter(Boolean)
      .sort((a,b)=>a.label.localeCompare(b.label));
  }, [staffAll]);

  const [form, setForm] = useState(defaultDraft());
  const [assigneeMode, setAssigneeMode] = useState("select");
  const isNew = !event?.id;

  // dark/light tokens
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const overlayBg = isDark ? "bg-black/70" : "bg-black/40";
  const panelBg   = isDark ? "bg-[#17191e]" : "bg-white";
  const borderCol = isDark ? "border-white/10" : "border-black/10";
  const labelCol  = isDark ? "text-white/80" : "text-black/70";
  const inputBg   = isDark ? "bg-white/10" : "bg-black/5";
  const inputText = isDark ? "text-white" : "text-black";
  const placeholder = isDark ? "placeholder-white/60" : "placeholder-black/50";

  const cancelBtn =
    isDark
      ? "text-white bg-white/10 hover:bg-white/20 border border-white/15"
      : "text-gray-800 bg-gray-100 hover:bg-gray-200 border border-black/10";
  const deleteBtn =
    isDark
      ? "bg-red-600/85 hover:bg-red-600 text-white"
      : "bg-red-600 hover:bg-red-500 text-white";
  const saveBtn = "bg-orange-500 hover:bg-orange-600 text-white";

  // Seed values whenever opened
  useEffect(() => {
    if (!open) return;
    if (event && event.id) {
      setForm({
        id: event.id,
        title: event.title || "",
        batch: event.batch || "",
        start: toLocalInput(event.start),
        end: toLocalInput(event.end),
        stage: stageLabelFromEvent(event),
        qty: event.qty ?? 1,
        category: event.category || "",
        taskType: event.taskType || "",
        productId: event.productId || "",
        assigneeId: event.assigneeId || "",
        color: event.color || "#f97316",
        notes: event.notes || "",
        tags: Array.isArray(event.tags) ? event.tags : [],
      });
    } else {
      setForm(defaultDraft());
    }
  }, [open, event]);

  useEffect(() => {
    setAssigneeMode(staffOptions.length ? "select" : "custom");
  }, [staffOptions.length]);

  const categories = useMemo(() => {
    const set = new Set();
    (events || []).forEach((e) => { if (e && e.category) set.add(e.category); });
    return Array.from(set).sort();
  }, [events]);

  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); doClose(); }
      if ((e.key === "Enter" && (e.metaKey || e.ctrlKey)) || (e.key === "Enter" && e.target?.tagName !== "TEXTAREA")) {
        e.preventDefault(); handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, form]);

  function doClose(){ onClose?.(); }
  function update(k, v){ setForm((prev) => ({ ...prev, [k]: v })); }

  async function handleSave(){
    const stagePatch = buildStagePatch(form.tags, form.stage);

    let assigneeId = form.assigneeId || "";
    if (assigneeMode === "select") {
      const valid = staffOptions.find((o) => o.id === assigneeId);
      if (!valid) assigneeId = "";
    }

    const payload = {
      id: form.id || String(Date.now()),
      title: form.title || "Untitled",
      start: fromLocalInput(form.start) || new Date().toISOString(),
      end: fromLocalInput(form.end) || new Date(Date.now()+60*60*1000).toISOString(),
      batch: form.batch || undefined,
      qty: Number(form.qty) || 1,
      category: form.category || undefined,
      taskType: form.taskType || undefined,
      productId: form.productId || undefined,
      assigneeId: assigneeMode === "custom" ? (assigneeId || undefined) : (assigneeId || undefined),
      color: form.color || undefined,
      notes: form.notes || "",
      ...stagePatch,
    };
    await upsertEvents([payload]);
    doClose();
  }

  async function handleDelete(){
    const delId = event?.id || event?._id || event?.uuid;
    if (!delId) return doClose();
    const ok = confirm("Delete this task?");
    if (!ok) return;
    const success = callDeleteAny(planner, delId);
    if (!success) {
      window.dispatchEvent(new CustomEvent("mudo:planner:delete", { detail: { id: delId } }));
    }
    doClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000]">
      <style>{`.mudo-modal select.mudo-select option{color:#111!important;background:#fff!important;}`}</style>

      <div className={`absolute inset-0 ${overlayBg}`} onClick={doClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4 mudo-modal">
        <div ref={rootRef} className={`w-full max-w-2xl rounded-xl ${panelBg} ${borderCol} border shadow-2xl`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm font-semibold">{!event?.id ? "New Task" : "Edit Task"}</div>
            <button onClick={doClose} className="px-2 py-1 rounded hover:bg-white/10" aria-label="Close">Close</button>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Title (auto-resizing textarea so long titles wrap) */}
              <Field label="Title" labelCol={labelCol}>
                <textarea
                  rows={2}
                  onInput={(e) => {
                    e.currentTarget.style.height = "auto";
                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                  }}
                  className={`w-full px-3 py-2 rounded resize-y ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.title}
                  onChange={(e)=>update("title", e.target.value)}
                  placeholder="new task"
                />
              </Field>

              <Field label="Batch (optional)" labelCol={labelCol}>
                <input
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.batch}
                  onChange={(e)=>update("batch", e.target.value)}
                  placeholder="e.g. B24-09"
                />
              </Field>

              <Field label="Start" labelCol={labelCol}>
                <input
                  type="datetime-local"
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.start}
                  onChange={(e)=>update("start", e.target.value)}
                />
              </Field>

              <Field label="End" labelCol={labelCol}>
                <input
                  type="datetime-local"
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.end}
                  onChange={(e)=>update("end", e.target.value)}
                />
              </Field>

              <Field label="Stage" labelCol={labelCol}>
                <select
                  className={`mudo-select w-full px-3 py-2 rounded ${inputBg} ${inputText} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.stage}
                  onChange={(e)=>update("stage", e.target.value)}
                >
                  <option value="">—</option>
                  {STAGE_LABELS.map((s)=> <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              <Field label="Qty" labelCol={labelCol}>
                <input
                  type="number" min="0"
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.qty}
                  onChange={(e)=>update("qty", e.target.value)}
                  placeholder="1"
                />
              </Field>

              <Field label="Category" labelCol={labelCol}>
                <input
                  list="mudo-categories"
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.category}
                  onChange={(e)=>update("category", e.target.value)}
                  placeholder="Admin / Production / Orders"
                />
                <datalist id="mudo-categories">
                  {Array.from(new Set((events||[]).map(e=>e?.category).filter(Boolean))).sort().map((c)=> <option key={c} value={c} />)}
                </datalist>
              </Field>

              <Field label="Task Type" labelCol={labelCol}>
                <input
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.taskType}
                  onChange={(e)=>update("taskType", e.target.value)}
                  placeholder="bisque / glaze / admin / ..."
                />
              </Field>

              <Field label="Product (optional)" labelCol={labelCol}>
                <input
                  className={`w-full px-3 py-2 rounded ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.productId}
                  onChange={(e)=>update("productId", e.target.value)}
                  placeholder="product id or name"
                />
              </Field>

              {/* Notes (full width on md) */}
              <div className="md:col-span-2">
                <Field label="Notes" labelCol={labelCol}>
                  <textarea
                    rows={4}
                    className={`w-full px-3 py-2 rounded resize-y ${inputBg} ${inputText} ${placeholder} outline-none focus:ring-2 ring-orange-500/60`}
                    value={form.notes}
                    onChange={(e)=>update("notes", e.target.value)}
                    placeholder="Optional details, blockers, glaze combos, etc."
                  />
                </Field>
              </div>

              {/* Assignee */}
              <Field label="Assignee" labelCol={labelCol}>
                <select
                  className={`mudo-select w-full px-3 py-2 rounded ${inputBg} ${inputText} outline-none focus:ring-2 ring-orange-500/60`}
                  value={form.assigneeId || ""}
                  onChange={(e)=>update("assigneeId", e.target.value)}
                >
                  <option value="">—</option>
                  {staffOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="flex items-center justify-between px-4 pb-4">
            <div className="space-x-2">
              {!!event?.id && (
                <button type="button" className={`px-3 py-1.5 rounded ${deleteBtn}`} onClick={handleDelete}>
                  Delete
                </button>
              )}
            </div>
            <div className="space-x-2">
              <button className={`px-3 py-1.5 rounded ${cancelBtn}`} onClick={doClose}>Cancel</button>
              <button className={`px-3 py-1.5 rounded ${saveBtn}`} onClick={handleSave}>
                Save Task
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- field wrapper ---------------------- */
function Field({ label, children, labelCol }) {
  return (
    <label className="text-sm">
      <div className={`${labelCol} mb-1`}>{label}</div>
      {children}
    </label>
  );
}
